/**
 * ── Billing → Module Linkage Hook ──
 * Bridges the commerce entitlement system with the module/capability gating system.
 *
 * Commerce entitlements (from billing_agreements → commerce_entitlements) define
 * what a customer has paid for. The module system (workspace_modules, platform_enabled_modules,
 * etc.) defines what's functionally available.
 *
 * This hook resolves which modules and capabilities should be active based on
 * the entity's billing state, and provides helpers for the entitlement access system.
 */

import { useMemo } from "react";
import { useEntityBillingResolution, type ResolvedEntitlement } from "@/hooks/useBillingResolution";
import { PLANS, type PlanId, PLAN_LIST, ADD_ONS } from "@/lib/commercial-catalog";

// ── Types ────────────────────────────────────────────────────────────────────

export interface BillingModuleResolution {
  /** The resolved plan tier from billing, if determinable */
  resolvedPlan: PlanId | null;
  /** Module keys that billing grants access to */
  billingGrantedModules: Set<string>;
  /** Capability keys that billing grants access to */
  billingGrantedCapabilities: Set<string>;
  /** Add-on IDs detected in active entitlements */
  activeAddOnIds: Set<string>;
  /** Whether billing state suggests the entity has paid access */
  hasPaidAccess: boolean;
  /** Feature keys from entitlements */
  activeFeatureKeys: Set<string>;
  /** Check if a specific module is granted by billing */
  isModuleGrantedByBilling: (moduleKey: string) => boolean;
  /** Check if a specific capability is granted by billing */
  isCapabilityGrantedByBilling: (capKey: string) => boolean;
  /** Get the plan that would unlock a given module (for upsell) */
  getUpgradePlanForModule: (moduleKey: string) => PlanId | null;
}

// ── Plan Detection ──────────────────────────────────────────────────────────

/**
 * Attempts to detect the plan tier from entitlement feature keys or product metadata.
 * This maps billing data back to our commercial catalog plan definitions.
 */
function detectPlanFromEntitlements(entitlements: ResolvedEntitlement[]): PlanId | null {
  // Check for feature entitlements that signal specific plans
  const featureKeys = new Set<string>();
  entitlements.forEach((e) => {
    if (e.featureKey) featureKeys.add(e.featureKey);
  });

  // Look for explicit plan feature markers
  // Convention: features named like "plan_enterprise", "plan_premium" etc.
  for (const plan of [...PLAN_LIST].reverse()) {
    if (featureKeys.has(`plan_${plan.id}`)) return plan.id;
  }

  // Heuristic: match by counting which plan's features overlap most
  let bestMatch: PlanId | null = null;
  let bestOverlap = 0;

  for (const plan of PLAN_LIST) {
    const planModules = new Set(plan.includedModules);
    const planCaps = new Set(plan.includedCapabilities);
    let overlap = 0;

    featureKeys.forEach((fk) => {
      if (planModules.has(fk) || planCaps.has(fk)) overlap++;
    });

    if (overlap > bestOverlap) {
      bestOverlap = overlap;
      bestMatch = plan.id;
    }
  }

  // Fallback: check product names for plan keywords
  if (!bestMatch) {
    const productNames = entitlements.map((e) => e.productName.toLowerCase()).join(" ");
    if (productNames.includes("enterprise")) return "enterprise";
    if (productNames.includes("premium")) return "premium";
    if (productNames.includes("professional") || productNames.includes("pro")) return "professional";
    if (productNames.includes("core") || productNames.includes("starter")) return "core";
  }

  return bestMatch;
}

// ── Main Hook ────────────────────────────────────────────────────────────────

export function useBillingToModules(
  entityId: string,
  entityType: "organization" | "association",
): BillingModuleResolution & { isLoading: boolean } {
  const { entitlements, activeFeatures, hasActiveBilling, isLoading } = useEntityBillingResolution(
    entityId,
    entityType,
  );

  const resolution = useMemo((): BillingModuleResolution => {
    if (!hasActiveBilling) {
      return {
        resolvedPlan: null,
        billingGrantedModules: new Set(),
        billingGrantedCapabilities: new Set(),
        activeAddOnIds: new Set(),
        hasPaidAccess: false,
        activeFeatureKeys: new Set(),
        isModuleGrantedByBilling: () => false,
        isCapabilityGrantedByBilling: () => false,
        getUpgradePlanForModule: (moduleKey) => {
          const plan = PLAN_LIST.find((p) => p.includedModules.includes(moduleKey));
          return plan?.id || null;
        },
      };
    }

    // Detect plan
    const resolvedPlan = detectPlanFromEntitlements(entitlements);

    // Build granted modules and capabilities from plan
    const billingGrantedModules = new Set<string>();
    const billingGrantedCapabilities = new Set<string>();

    if (resolvedPlan) {
      const plan = PLANS[resolvedPlan];
      plan.includedModules.forEach((m) => {
        billingGrantedModules.add(m);
      });
      plan.includedCapabilities.forEach((c) => {
        billingGrantedCapabilities.add(c);
      });

      // Also include all lower-tier plan modules/capabilities
      for (const p of PLAN_LIST) {
        if (p.sortOrder <= plan.sortOrder) {
          p.includedModules.forEach((m) => {
            billingGrantedModules.add(m);
          });
          p.includedCapabilities.forEach((c) => {
            billingGrantedCapabilities.add(c);
          });
        }
      }
    }

    // Detect active add-ons from entitlement feature keys
    const activeAddOnIds = new Set<string>();
    for (const addon of ADD_ONS) {
      const addonModules = new Set(addon.modules);
      const addonCaps = new Set(addon.capabilities);
      const hasAddonModule = [...activeFeatures].some((f) => addonModules.has(f) || addonCaps.has(f));
      if (hasAddonModule) {
        activeAddOnIds.add(addon.id);
        addon.modules.forEach((m) => {
          billingGrantedModules.add(m);
        });
        addon.capabilities.forEach((c) => {
          billingGrantedCapabilities.add(c);
        });
      }
    }

    // Also add any feature keys as potential module/capability grants
    activeFeatures.forEach((fk) => {
      // Convention: feature keys might directly be module or capability keys
      billingGrantedModules.add(fk);
      billingGrantedCapabilities.add(fk);
    });

    return {
      resolvedPlan,
      billingGrantedModules,
      billingGrantedCapabilities,
      activeAddOnIds,
      hasPaidAccess: true,
      activeFeatureKeys: activeFeatures,
      isModuleGrantedByBilling: (moduleKey) => billingGrantedModules.has(moduleKey),
      isCapabilityGrantedByBilling: (capKey) => billingGrantedCapabilities.has(capKey),
      getUpgradePlanForModule: (moduleKey) => {
        if (billingGrantedModules.has(moduleKey)) return null; // already have it
        const plan = PLAN_LIST.find((p) => p.includedModules.includes(moduleKey));
        return plan?.id || null;
      },
    };
  }, [entitlements, activeFeatures, hasActiveBilling]);

  return { ...resolution, isLoading };
}
